/**
 * What Arrange says of an arrangement: one line over the page, and the rest behind Details.
 *
 * The line (`lib/arrange-said`) is for the reader who gave a score and named its players: who
 * it was arranged for, how much of the score is in it, what was added. Details opens what the
 * lab reads a run by, unchanged — what a player would find awkward (`ArrangeIdiom`) and who
 * plays what, counted (`ArrangeReading`, `TunedReading`) — for the reader who wants to know
 * why. It closes again on every new arrangement.
 */

import { useEffect, useState } from "react";
import type { ArrangePayload, Seat } from "@app/shared-types";
import { arrangeSaid } from "../lib/arrange-said";
import ArrangeIdiom from "./ArrangeIdiom";
import ArrangeReading from "./ArrangeReading";
import TunedReading from "./TunedReading";

export interface ArrangeSummaryProps {
  readonly result: ArrangePayload;
  /** The players it was arranged for. */
  readonly roster: readonly string[];
  readonly seats: readonly Seat[];
}

export default function ArrangeSummary({
  result,
  roster,
  seats,
}: ArrangeSummaryProps): JSX.Element {
  const [open, setOpen] = useState(false);
  useEffect(() => setOpen(false), [result]);

  return (
    <>
      <div className="flex shrink-0 flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-shell-edge bg-shell px-4 py-2">
        <p role="status" className="min-w-0 flex-1 basis-80 py-1 text-[14px] text-shell-lit">
          {arrangeSaid(result, roster, seats)}
        </p>
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
          className="h-11 shrink-0 rounded-md px-2 text-[13px] text-shell-text underline decoration-shell-dim underline-offset-2 hover:text-shell-lit"
        >
          {open ? "Hide details" : "Details"}
        </button>
      </div>
      {!open ? null : (
        <div className="max-h-[40%] shrink-0 overflow-y-auto">
          {result.start === "tune" ? null : <ArrangeIdiom idiom={result.idiom} />}
          {result.start === "tune" ? (
            <TunedReading result={result} />
          ) : (
            <ArrangeReading result={result} />
          )}
        </div>
      )}
    </>
  );
}
